/**
 * game-end.ts — R9 rework (WEB.4.UI.10a)
 *
 * Pure detection helpers for game-ending conditions (R9 spec):
 *
 * Trigger order (first match wins):
 *   1. Tipping Point Passed — total pollution >= 600 AND round >= 4 → all lose (ending-C)
 *   2. Mutual Destruction  — all 4 seats bankrupt → all lose (ending-D)
 *   3. Bankruptcy Solo Survivor — only 1 active seat left → solo win (ending-B)
 *   4. Renewable Pathway   — round>=18, >=60% built facilities are Renewable,
 *                            total pollution <300 → coop win (ending-A)
 *   5. Economic Hegemony   — round>=5, one seat has >=2× next-richest tokens,
 *                            owns >=35% of all built facilities → solo win (ending-B)
 *   6. Round 30 default    — at end of round 30, highest score wins.
 *                            score = tokens + renewables×10 + energy×0.5 − pollution×1.5 (R20)
 *                            Maps to ending-A if score>50 AND renewables>=3 else ending-B.
 */

import { FACILITIES } from "./facilities.js";

export type SeatIndex = 0 | 1 | 2 | 3;
export type GameStatus = "playing" | "ended";

/**
 * R9: expanded EndReason set.
 *   ending-A → cooperative / renewable victory (green_future / round-limit top-score clean)
 *   ending-B → solo economic victory (bankruptcy survivor / economic hegemony / round-limit)
 *   ending-C → tipping point all-lose
 *   ending-D → mutual destruction all-lose
 */
export type EndReason =
  | "bankruptcy-solo-survivor" // one seat left active → ending-B
  | "tipping-point" // total pollution >= TIPPING_POINT_THRESHOLD → ending-C (all lose)
  | "mutual-destruction" // all 4 bankrupt → ending-D (all lose)
  | "renewable-pathway" // coop renewable win → ending-A
  | "economic-hegemony" // solo economic win → ending-B
  | "round-limit" // round 30 default → ending-A or ending-B by score
  | null;

/**
 * Narrative ending bucket.
 * AB (Builders) can extend GameOverModal based on this.
 */
export type EndingBucket = "ending-A" | "ending-B" | "ending-C" | "ending-D";

export interface GameEndState {
  status: GameStatus;
  reason: EndReason;
  /** Narrative ending bucket (null when game is still playing). */
  ending: EndingBucket | null;
  /** Seats marked bankrupt — out of the game. */
  lostSeats: ReadonlySet<SeatIndex>;
  /** Seat that WON (solo). null for all-win or all-lose outcomes. */
  winnerSeat: SeatIndex | null;
  /** For coop wins: all active seats. For solo: the winner only. */
  winnerSeats: ReadonlyArray<SeatIndex>;
}

// ---------------------------------------------------------------------------
// R9 constants (bumped from R8 audit values)
// ---------------------------------------------------------------------------

/**
 * Total pollution cap (all lose) → ending-C.
 *
 * R11.2.6 recalibration: raised 150 → 200.
 * CF-1 expansion: scaled 200 → 600 (3× round expansion 10 → 30).
 * Rationale: 4 seats × avg 4.5 pollution/facility/turn × 1 facility each ≈ 18/round.
 * 600 / 18 ≈ 33 rounds at average rate; allows spikes from disaster cards while
 * giving players the mandated ≥18 rounds of play.
 */
export const TIPPING_POINT_THRESHOLD = 600;

/**
 * Minimum round before tipping-point can fire.
 * Safety net: even if threshold is hit early (mass disaster cards) the
 * game will NOT end before round 4, giving players a first-pass chance.
 */
export const TIPPING_POINT_MIN_ROUND = 4;

/**
 * Renewable Pathway: minimum round before it can fire.
 * CF-1 expansion: scaled 6 → 18 (3× round expansion 10 → 30).
 */
export const RENEWABLE_PATHWAY_MIN_ROUND = 18;

/** Renewable Pathway: fraction of all built facilities that must be Renewable. */
export const RENEWABLE_PATHWAY_RATIO = 0.6;

/**
 * Renewable Pathway: total pollution must be below this.
 * CF-1 expansion: scaled 100 → 300 (3× round expansion 10 → 30).
 */
export const RENEWABLE_PATHWAY_POLLUTION_CAP = 300;

/**
 * Economic Hegemony: minimum round before it can fire.
 *
 * R19 rebalance (option B locked): raised 5 → 14.
 * Rationale: Era-3 boundary; harder lock for clearly-dominant-leader scenarios.
 * Playtest showed too-easy wins by round 5–10 with prior values.
 */
export const ECONOMIC_HEGEMONY_MIN_ROUND = 14;

/**
 * Economic Hegemony: leader must have at least this multiple of next-richest tokens.
 *
 * R19 rebalance (option B locked): raised 2 → 3.
 * Rationale: Era-3 boundary; harder lock for clearly-dominant-leader scenarios.
 * Playtest showed too-easy wins by round 5–10 with prior values.
 */
export const ECONOMIC_HEGEMONY_TOKEN_RATIO = 3;

/**
 * Economic Hegemony: leader must own at least this fraction of all built facilities.
 *
 * R19 rebalance (option B locked): raised 0.35 → 0.45.
 * Rationale: Era-3 boundary; harder lock for clearly-dominant-leader scenarios.
 * Playtest showed too-easy wins by round 5–10 with prior values.
 */
export const ECONOMIC_HEGEMONY_FACILITY_RATIO = 0.45;

/**
 * Round limit — game ends if round > ROUND_LIMIT.
 * CF-1 expansion: scaled 10 → 30.
 */
export const ROUND_LIMIT = 30;

/** Round-limit: score threshold for ending-A (cooperative bucket) vs ending-B (solo). */
export const ROUND_LIMIT_ENDING_A_SCORE_THRESHOLD = 50;

/**
 * Round-limit ending-A: winner must own this many renewables.
 * CF-1: scaled 3 → 9 (proportional to 3× game length; reflects longer accumulation window).
 */
export const ROUND_LIMIT_ENDING_A_RENEWABLE_MIN = 9;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALL_SEATS: ReadonlyArray<SeatIndex> = [0, 1, 2, 3];

/**
 * Player-count support: the seats actually IN the game for a given numPlayers (2|3|4).
 * Default 4 → byte-identical to the historical `ALL_SEATS`. Seats >= numPlayers are
 * "not in game" (they keep INITIAL state and are excluded from all win/end logic).
 */
export function seatsInGame(numPlayers: 2 | 3 | 4 = 4): SeatIndex[] {
  return ALL_SEATS.filter((s) => s < numPlayers);
}

/**
 * Seats that are present (< numPlayers) AND not marked bankrupt.
 * `numPlayers` defaults to 4 so every existing caller is byte-identical (all 4 seats
 * are in-game). For 2/3-player games the absent seats (index >= numPlayers) are
 * excluded so an untouched INITIAL seat can never be counted as a survivor/winner.
 */
export function activeSeats(
  lostSeats: ReadonlySet<SeatIndex>,
  numPlayers: 2 | 3 | 4 = 4,
): SeatIndex[] {
  return seatsInGame(numPlayers).filter((s) => !lostSeats.has(s));
}

/** Total renewable facilities built (across all seats, all tiles). */
export function countAllRenewables(
  builtFacilities: Record<number, string | undefined>,
  _ownedBy: Record<number, number | undefined>,
  _seats: ReadonlyArray<SeatIndex>,
): number {
  let count = 0;
  for (const facilityId of Object.values(builtFacilities)) {
    if (facilityId === undefined) continue;
    const def = FACILITIES.find((f) => f.id === facilityId);
    if (def?.category === "Renewable") count++;
  }
  return count;
}

/** Total built facilities (any category). */
export function countAllBuilt(builtFacilities: Record<number, string | undefined>): number {
  return Object.values(builtFacilities).filter((v) => v !== undefined).length;
}

/** Count renewable facilities owned by a specific seat. */
function countRenewableForSeat(
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, number | undefined>,
  seat: SeatIndex,
): number {
  let count = 0;
  for (const [tileIdxStr, facilityId] of Object.entries(builtFacilities)) {
    if (facilityId === undefined) continue;
    const tileIdx = Number(tileIdxStr);
    if (ownedBy[tileIdx] !== seat) continue;
    const def = FACILITIES.find((f) => f.id === facilityId);
    if (def?.category === "Renewable") count++;
  }
  return count;
}

/** Count facilities owned by a specific seat (any category). */
function countBuiltForSeat(
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, number | undefined>,
  seat: SeatIndex,
): number {
  let count = 0;
  for (const [tileIdxStr, facilityId] of Object.entries(builtFacilities)) {
    if (facilityId === undefined) continue;
    const tileIdx = Number(tileIdxStr);
    if (ownedBy[tileIdx] === seat) count++;
  }
  return count;
}

/**
 * R9 seat score formula for round-limit tie-breaker:
 *   tokens + renewables×10 + energy×0.5 − pollution×1.5
 *
 * R20 rebalance: Pollution weight 3× stricter (0.5→1.5) — playtest showed
 * players with high pollution still winning round-limit by stockpiling tokens.
 * ROUND_LIMIT_ENDING_A_SCORE_THRESHOLD remains 50; high-pollution seats now
 * genuinely cannot hit it without heavy renewable investment.
 */
function seatScore(
  tokens: ReadonlyArray<number>,
  energy: ReadonlyArray<number>,
  pollution: ReadonlyArray<number>,
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, number | undefined>,
  seat: SeatIndex,
): number {
  const renewables = countRenewableForSeat(builtFacilities, ownedBy, seat);
  return (
    (tokens[seat] ?? 0) + renewables * 10 + (energy[seat] ?? 0) * 0.5 - (pollution[seat] ?? 0) * 1.5
  );
}

// ---------------------------------------------------------------------------
// Detection (pure)
// ---------------------------------------------------------------------------

export interface DetectionInputs {
  tokens: ReadonlyArray<number>;
  energy: ReadonlyArray<number>;
  pollution: ReadonlyArray<number>;
  round: number;
  lostSeats: ReadonlySet<SeatIndex>;
  builtFacilities: Record<number, string | undefined>;
  ownedBy: Record<number, number | undefined>;
}

/**
 * Top-level end-condition detection. Returns a new GameEndState describing
 * whether the game has ended, why, and who won/lost.
 *
 * R9 trigger order (first match wins):
 *   1. Tipping Point Passed (all lose, → ending-C) — total pollution >= 600 AND round >= 4
 *   2. Mutual Destruction (all lose, → ending-D) — all 4 seats bankrupt
 *   3. Bankruptcy Solo Survivor (solo win, → ending-B) — 1 active seat left
 *   4. Renewable Pathway (coop win, → ending-A) — round>=18, >=60% renewable, pollution<300
 *   5. Economic Hegemony (solo win, → ending-B) — round>=5, >=2× tokens, >=35% facilities
 *   6. Round 30 default — highest R9 score; ending-A if clean else ending-B
 *
 * Bankruptcy detection itself is NOT done here — callers add the bankrupt seat
 * to lostSeats then re-run detect() to check whether the game has ended.
 */
export function detectGameEnd(
  inputs: DetectionInputs,
  lostSeatsExtended: ReadonlySet<SeatIndex>,
  numPlayers: 2 | 3 | 4 = 4,
): GameEndState {
  // Seats >= numPlayers are NOT in the game (default 4 → all seats present, byte-identical).
  // Their pollution stays at INITIAL 0, so summing across all four slots is unchanged.
  const totalPollution =
    (inputs.pollution[0] ?? 0) +
    (inputs.pollution[1] ?? 0) +
    (inputs.pollution[2] ?? 0) +
    (inputs.pollution[3] ?? 0);

  // 1. Tipping Point Passed — total pollution >= threshold AND round >= min → ending-C.
  if (totalPollution >= TIPPING_POINT_THRESHOLD && inputs.round >= TIPPING_POINT_MIN_ROUND) {
    return {
      status: "ended",
      reason: "tipping-point",
      ending: "ending-C",
      lostSeats: lostSeatsExtended,
      winnerSeat: null,
      winnerSeats: [],
    };
  }

  // 2. Mutual Destruction — all in-game seats bankrupt → ending-D.
  if (lostSeatsExtended.size >= numPlayers) {
    return {
      status: "ended",
      reason: "mutual-destruction",
      ending: "ending-D",
      lostSeats: lostSeatsExtended,
      winnerSeat: null,
      winnerSeats: [],
    };
  }

  const active = activeSeats(lostSeatsExtended, numPlayers);

  // 3. Bankruptcy Solo Survivor — 1 active seat left → ending-B.
  if (active.length === 1) {
    const survivor = active[0];
    if (survivor !== undefined) {
      return {
        status: "ended",
        reason: "bankruptcy-solo-survivor",
        ending: "ending-B",
        lostSeats: lostSeatsExtended,
        winnerSeat: survivor,
        winnerSeats: [survivor],
      };
    }
  }

  const totalBuilt = countAllBuilt(inputs.builtFacilities);
  const renewableRatio =
    totalBuilt > 0
      ? countAllRenewables(inputs.builtFacilities, inputs.ownedBy, ALL_SEATS) / totalBuilt
      : 0;

  // 4. Renewable Pathway — coop win → ending-A.
  if (
    active.length > 0 &&
    inputs.round >= RENEWABLE_PATHWAY_MIN_ROUND &&
    renewableRatio >= RENEWABLE_PATHWAY_RATIO &&
    totalPollution < RENEWABLE_PATHWAY_POLLUTION_CAP
  ) {
    return {
      status: "ended",
      reason: "renewable-pathway",
      ending: "ending-A",
      lostSeats: lostSeatsExtended,
      winnerSeat: null,
      winnerSeats: active,
    };
  }

  // 5. Economic Hegemony — solo win → ending-B.
  if (active.length >= 2 && inputs.round >= ECONOMIC_HEGEMONY_MIN_ROUND && totalBuilt > 0) {
    // Find richest active seat.
    let richestSeat: SeatIndex | undefined;
    let richestTokens = -1;
    for (const s of active) {
      const t = inputs.tokens[s] ?? 0;
      if (t > richestTokens) {
        richestTokens = t;
        richestSeat = s;
      }
    }

    if (richestSeat !== undefined) {
      // Find next-richest (must be a different seat).
      let nextRichestTokens = -1;
      for (const s of active) {
        if (s === richestSeat) continue;
        const t = inputs.tokens[s] ?? 0;
        if (t > nextRichestTokens) nextRichestTokens = t;
      }

      const facilityFraction =
        countBuiltForSeat(inputs.builtFacilities, inputs.ownedBy, richestSeat) / totalBuilt;
      const tokenRatioMet =
        nextRichestTokens <= 0
          ? richestTokens > 0 // any positive tokens vs zero = hegemony
          : richestTokens / nextRichestTokens >= ECONOMIC_HEGEMONY_TOKEN_RATIO;

      if (tokenRatioMet && facilityFraction >= ECONOMIC_HEGEMONY_FACILITY_RATIO) {
        return {
          status: "ended",
          reason: "economic-hegemony",
          ending: "ending-B",
          lostSeats: lostSeatsExtended,
          winnerSeat: richestSeat,
          winnerSeats: [richestSeat],
        };
      }
    }
  }

  // 6. Round 30 default — highest R9 score wins.
  if (inputs.round > ROUND_LIMIT && active.length > 0) {
    const firstSeat = active[0];
    if (firstSeat !== undefined) {
      let bestSeat: SeatIndex = firstSeat;
      let bestScore = seatScore(
        inputs.tokens,
        inputs.energy,
        inputs.pollution,
        inputs.builtFacilities,
        inputs.ownedBy,
        bestSeat,
      );

      for (const s of active) {
        const score = seatScore(
          inputs.tokens,
          inputs.energy,
          inputs.pollution,
          inputs.builtFacilities,
          inputs.ownedBy,
          s,
        );
        if (score > bestScore) {
          bestScore = score;
          bestSeat = s;
        }
      }

      const winnerRenewables = countRenewableForSeat(
        inputs.builtFacilities,
        inputs.ownedBy,
        bestSeat,
      );
      const ending: EndingBucket =
        bestScore > ROUND_LIMIT_ENDING_A_SCORE_THRESHOLD &&
        winnerRenewables >= ROUND_LIMIT_ENDING_A_RENEWABLE_MIN
          ? "ending-A"
          : "ending-B";

      return {
        status: "ended",
        reason: "round-limit",
        ending,
        lostSeats: lostSeatsExtended,
        winnerSeat: bestSeat,
        winnerSeats: [bestSeat],
      };
    }
  }

  // Game continues.
  return {
    status: "playing",
    reason: null,
    ending: null,
    lostSeats: lostSeatsExtended,
    winnerSeat: null,
    winnerSeats: [],
  };
}

/**
 * Helper: would the drawer go bankrupt if forced to pay `amount`?
 * Returns true when amount > balance AND amount > 0.
 * Used by toll-on-landing flow to detect forced payment failures.
 */
export function wouldGoBankrupt(balance: number, amount: number): boolean {
  return amount > 0 && balance < amount;
}

/**
 * N-2 Climate Crisis Vote — proportional pollution reduction.
 *
 * Distributes `totalReduction` across 4 seats proportionally to each seat's
 * current pollution share. Heavier polluters benefit more from the cleanup.
 *
 * seat_reduction[i] = round(totalReduction × (pollution[i] / sumPollution))
 *
 * Edge cases:
 *  - sumPollution === 0: distribute equally (round(totalReduction / 4) per seat).
 *  - result floors at 0 (cannot go negative).
 *  - Rounding may cause sum to differ from totalReduction by ±1 per seat; this is
 *    intentional (per-seat integer math — no fractional pollution).
 */
export function computeProportionalPollutionReduction(
  pollution: readonly [number, number, number, number],
  totalReduction: number,
  numPlayers: 2 | 3 | 4 = 4,
): [number, number, number, number] {
  // Codex Phase 2a HIGH 1 fix: largest-remainder method. Math.round per seat
  // could over/under-remove the requested total (e.g. [25,25,25,25],30 → 32;
  // [1,1,1,97],30 → 29). Largest-remainder guarantees sum equals the cap.
  if (totalReduction <= 0) return [0, 0, 0, 0];
  // Sum + distribute over PRESENT seats only. Default 4 → byte-identical (all
  // four slots in). Seats >= numPlayers have INITIAL pollution 0 anyway, so the
  // `< numPlayers` guard is defensive — an inactive slot contributes 0 and the
  // remainder loop below skips it (its exact share is 0 → never +1'd).
  let sum = 0;
  for (let i = 0; i < numPlayers; i++) sum += pollution[i] ?? 0;
  if (sum <= 0) {
    // Nobody polluted → nothing to clean. Return zeros (NOT a free equal-split
    // refund — fixes the previous "credit clean seats" bug).
    return [0, 0, 0, 0];
  }
  const cap = Math.min(totalReduction, sum);
  // Absent seats (>= numPlayers) receive 0 — share zeroed so distribution matches the
  // present-seats-only sum above. Default 4 → all four get their proportional share,
  // byte-identical. (In real play absent seats have INITIAL pollution 0 anyway.)
  const share = (i: number): number => (i < numPlayers ? cap * ((pollution[i] ?? 0) / sum) : 0);
  const exact: [number, number, number, number] = [share(0), share(1), share(2), share(3)];
  const floored: [number, number, number, number] = [
    Math.floor(exact[0]),
    Math.floor(exact[1]),
    Math.floor(exact[2]),
    Math.floor(exact[3]),
  ];
  let remaining = cap - (floored[0] + floored[1] + floored[2] + floored[3]);
  // Distribute the remainder to seats with the largest fractional part first.
  const remainders = exact.map((v, i) => ({ idx: i, frac: v - Math.floor(v) }));
  remainders.sort((a, b) => b.frac - a.frac);
  for (const r of remainders) {
    if (remaining <= 0) break;
    const idx = r.idx as 0 | 1 | 2 | 3;
    // Don't exceed the seat's actual pollution (no negative pollution).
    if (floored[idx] < pollution[idx]) {
      floored[idx] = floored[idx] + 1;
      remaining -= 1;
    }
  }
  // Defensive clamp — should be redundant after cap+largest-remainder logic.
  return [
    Math.min(floored[0], pollution[0]),
    Math.min(floored[1], pollution[1]),
    Math.min(floored[2], pollution[2]),
    Math.min(floored[3], pollution[3]),
  ];
}
